/** Carry a selection across a campaign switch, or drop it.
 *
 *  THE BUG THIS EXISTS FOR. Cut.tsx set its universe with `setUniverseId((p) => p || fallback)`.
 *  `p` is the PREVIOUS campaign's universe id and it is truthy, so the switch kept it. Every
 *  request keyed on that id then asked the new campaign for a universe it cannot see and came back
 *  empty — an empty map that only a page reload fixed. Worse, `Select` falls back to rendering
 *  `options[0]` when the value matches nothing, so the screen NAMED a universe it was not using.
 *
 *  The rule, in order: keep what is selected if this campaign still has it; otherwise fall back to
 *  what was remembered, if this campaign has that; otherwise the first option — or nothing, when
 *  the selection is optional (a survey is a choice; a universe is what you are cutting from).
 *
 *  `saved` is deliberately checked against the SAME list rather than trusted: a remembered id is
 *  just as capable of belonging to another campaign as a live one.
 */
export function keepOrReset(
  current: string,
  options: { id: string }[],
  saved?: string,
  opts: { required?: boolean } = {},
): string {
  const { required = true } = opts;
  const has = (id?: string) => !!id && options.some((o) => o.id === id);
  if (has(current)) return current;
  if (has(saved)) return saved!;
  return required ? (options[0]?.id ?? "") : "";
}


/** The label a picker shows when its value matches none of its options.
 *
 *  THE SECOND HALF OF THE SAME BUG. `Select` rendered `options[0]`'s label whenever the value
 *  matched nothing, so a stale id did not merely filter to nothing — the control NAMED a universe,
 *  or a canvasser, that it was not using. Caleb saw a dropdown reading "Samuel Garcia" on a
 *  campaign Samuel is not on. A picker that cannot say what is selected must say that, not guess.
 *
 *  An EMPTY value keeps the old fallback on purpose: every picker in the app that starts empty
 *  pairs it with a first row that means "all" or "— select —", and the pages read "" as
 *  not-chosen-yet. It is a non-empty value with no matching option that is always a bug, and that
 *  is the only case this changes.
 */
export function selectLabel(value: string, options: [string, string][], missing = "\u2014 select \u2014"): string {
  const hit = options.find(([v]) => v === value);
  if (hit) return hit[1];
  return value ? missing : (options[0]?.[1] ?? "");
}

/** Keep a canvasser/user filter across a switch. Rosters key on `user_id`, not `id`. */
export function keepOrResetUser(current: string, roster: { user_id: string }[]): string {
  return keepOrReset(current, roster.map((c) => ({ id: c.user_id })), undefined, { required: false });
}


/** What a multi-picker's closed button says.
 *
 *  SAY WHAT IS PICKED, not just how many, while that still fits. This was written inline inside
 *  MultiSelect; it is a named function now because the phone needs the same rule (2026-10-01) and
 *  the two builds share no code, so the only thing that can keep one rule from becoming two is a
 *  test exercising both copies. `app/test/selection_test.cjs` does that.
 *
 *  `names` is the selected options' LABELS in the OPTIONS' order, not the order they were clicked,
 *  so the summary does not reshuffle itself while somebody is picking.
 */
export function pickerSummary(names: string[], label: string, noun?: [string, string]): string {
  if (names.length === 0) return label;
  if (names.length <= 2) return names.join(", ");
  return `${names.length} ${noun ? noun[1] : "selected"}`;
}
